import Sidebar from '@Components/Sidebars/Sidebar'
import React, { useCallback } from 'react'
import Button from '@Components/Buttons/Button'
import { StatusUpdate } from '@Entities/statements/status-update'
import paymentStatusDefinitions from '@App/const/payment-status-definitions'
import './index.scss'
import dayjs from 'dayjs'
import { PayeeRoyalties } from '@Entities/statements/payee-royalties'

interface RoyaltiesSidebarProps {
    closeSidebar: () => void
    data?: PayeeRoyalties
}

const RoyaltiesSidebar = (props: RoyaltiesSidebarProps) => {
    const { closeSidebar, data } = props

    if (!data) {
        return
    }

    const mapStatus = (status: string, updateTime?: string) => ({
        time: updateTime ? dayjs(updateTime).format('hh:mma') : null,
        status,
        ...paymentStatusDefinitions[status.toLocaleLowerCase()],
    })

    const processStatusHistory = useCallback(() => {
        let tempDay = ''
        let indexCount = -1
        const updates = data.statusUpdates
            .slice()
            .sort((a: StatusUpdate, b: StatusUpdate) => (new Date(a.updateTime) < new Date(b.updateTime) ? 1 : -1))
            .reduce((result: any[], item: StatusUpdate) => {
                let status = item.updateStatus ? item.updateStatus : 'Submitted'
                if (status === 'PendingPayerFunds') {
                    status = 'Pending Funds'
                } else if (status === 'FraudReview') {
                    status = 'Risk Review'
                }

                if (tempDay !== dayjs(item.updateTime).format('dddd, MMMM DD, YYYY')) {
                    tempDay = dayjs(item.updateTime).format('dddd, MMMM DD, YYYY')
                    indexCount++
                    result.push({
                        [tempDay]: [mapStatus(status, item.updateTime)],
                    })
                } else {
                    result[indexCount][tempDay].push(mapStatus(status, item.updateTime))
                }
                return result
            }, [])

        if (data.status === 'Transferred') {
            // Transferred status has no date/time associated with it so we push it to the bottom of the status updates list
            const keys = Object.keys(updates[updates.length - 1])
            updates[updates.length - 1][keys[0]].push(mapStatus(data.status))
        }
        return updates
    }, [data])

    return (
        <Sidebar onClose={closeSidebar}>
            <div className="status-sidebar">
                <>
                    <div className="status-head">
                        <h2>Payment Status</h2>
                        <p className="large grey-text">Payment status will be reflected here as it is updated.</p>
                    </div>
                    {processStatusHistory().map((day: any[], index: number) => (
                        <React.Fragment key={index}>
                            <p className="small bold date">{Object.keys(day)[0]}</p>
                            {Object.values(day)[0].map((update: any, nestedIndex: number) => (
                                <div className="status-block" key={nestedIndex}>
                                    <div className="status-circle-block">
                                        <div className={`status-circle ${update.color}`} />
                                    </div>
                                    <div className="status-info">
                                        <div className="status-row">
                                            {update.time && <p className="small bold time">{update.time}</p>}
                                            <p className="small">{update.status}</p>
                                        </div>
                                        <p className="small description grey-text">{update.explanation}</p>
                                        {update.link?.includes('methods') && (
                                            <p className="small description grey-text">
                                                For more information on payment methods, click{' '}
                                                <a
                                                    className="small description"
                                                    href="https://help.themlc.com/en/support/what-are-the-options-for-payment-methods"
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    here
                                                </a>
                                            </p>
                                        )}
                                        {update.link?.includes('contactus') && (
                                            <p className="small description grey-text">
                                                {' '}
                                                For assistance, please contact The MLC Support Team{' '}
                                                <a
                                                    className="small description"
                                                    href="https://portal.themlc.com/contact-us"
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    here.
                                                </a>
                                            </p>
                                        )}
                                        {update.link?.includes('statements') && (
                                            <div className="small description grey-text">
                                                You can also click
                                                <a
                                                    className="small description"
                                                    href="https://help.themlc.com/en/support/how-can-i-access-my-statement-in-the-portal"
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    {' '}
                                                    here{' '}
                                                </a>
                                                for steps to download statements.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </React.Fragment>
                    ))}
                    <div className="close-button">
                        <Button mainColor type="primary" onClick={closeSidebar}>
                            Close
                        </Button>
                    </div>
                </>
            </div>
        </Sidebar>
    )
}

export default RoyaltiesSidebar
